import React, { ComponentProps, PropsWithChildren, createContext, useMemo } from 'react';

import { BrowserRouter as ReactRouter } from 'react-router-dom';

import { useAuthState } from '../hooks/useAuth';
import type { AuthContextValue } from '../types';

const defaultAuthContext = new Proxy({} as AuthContextValue, {
  get(_target, property) {
    if (typeof property === 'symbol') {
      return undefined;
    }

    throw new Error(`AuthContext.${property} was accessed outside the auth provider.`);
  },
});

export const AuthContext = createContext<AuthContextValue>(defaultAuthContext);

type AuthRouterProps = ComponentProps<typeof ReactRouter>;

function AuthProvider({ children }: PropsWithChildren) {
  const state = useAuthState();

  const value = useMemo(
    () => ({
      loading: state.loading,
      authenticated: state.authenticated,
      token: state.token ?? undefined,
      user: state.user ?? undefined,
    }),
    [state.authenticated, state.loading, state.token, state.user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function AuthRouter(props: AuthRouterProps) {
  return (
    <AuthProvider>
      <ReactRouter {...props} />
    </AuthProvider>
  );
}
